import React from 'react'
// libs
import styled from 'styled-components'
// components
import { AvatarsList, PopoverModal } from 'components/UI'
// context
import { CreateReviewBLContext } from 'containers/Review/CreateReview/layers/BL'
import { NodeContentBLContext } from 'containers/ObjectivesList/components/NodeContent/layers/BL'
// icons
import DotsIconOrigin from '@mui/icons-material/MoreHorizOutlined'
// themes
import { colors } from 'themes'
import { TriggerWrapper } from 'components/UI/HoverablePopover'
import { Chip, ReviewIcon } from 'themes/views/tables'
import WatchLaterIcon from '@mui/icons-material/WatchLaterOutlined'
// types
import { ILinkingItem } from 'types/graphql'
// view components
import PlanItemActionsMenu from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanItemActionsMenu'
// views
import { AvatarWrapper } from 'components/PageComponents/Home/components/DashboardWidgets/components/StrategyActions/components/Content/components/TasksList/views'
import { LinkIcon, LinkIconWithCounter } from 'themes/views/objectivesList'
// utils
import { size } from 'polished'
import { isEmpty } from 'lodash'
import { isColorLight } from 'utils/tree'

// styled
const AdditionalInfo = styled.div<{
  $isLimitReached: boolean
}>`
  align-items: center;
  display: flex;
  margin-left: auto;
  padding-right: 8px;

  & > div,
  svg {
    margin-left: 8px;
  }

  ${LinkIcon} {
    margin-right: 0;
  }

  ${TriggerWrapper} {
    height: unset;
    max-width: unset;
    overflow: unset;
  }

  ${AvatarWrapper} {
    margin-right: ${({ $isLimitReached }) => ($isLimitReached ? 0 : '10px')};
  }
`

export const DotsIcon = styled(DotsIconOrigin)<{ $background: string; $isRootObjective?: boolean }>`
  && {
    cursor: pointer;
    ${size('24px')};

    path {
      fill: ${({ $background, $isRootObjective }) =>
        $isRootObjective && !isColorLight($background) ? colors.white : colors.black};
    }
  }
`

// constants
const ICON_STYLE = { color: colors.white }

const PAPER_PROPS = {
  style: {
    borderRadius: 4
  }
}

// types
interface IIncomingProps {
  node: any
  tree: any
  isKPI: boolean
  isRoot: boolean
  isAction: boolean
  background: string
  isObjective: boolean
  handleKPIAdd: () => void
  handleActionAdd: () => void
  handleObjectiveAdd: () => void
  nextLevelDescription: string
  shouldShowAddKPIButton: boolean
  shouldShowAddActionButton: boolean
  shouldShowAddObjectiveButton: boolean
}

const NodeInfo: React.FC<IIncomingProps> = ({
  node,
  tree,
  isKPI,
  isRoot,
  isAction,
  background,
  handleKPIAdd,
  isObjective,
  handleActionAdd,
  handleObjectiveAdd,
  nextLevelDescription,
  shouldShowAddKPIButton,
  shouldShowAddActionButton,
  shouldShowAddObjectiveButton
}) => {
  // context
  const { nodeEvents } = React.useContext(NodeContentBLContext) as any
  const { isReview } = React.useContext(CreateReviewBLContext)

  // constants
  const linkingItems: ILinkingItem[] = [
    ...node.linkingItems.destinationLinkingItems,
    ...node.linkingItems.sourceLinkingItems
  ]

  const isNodeHaveLinkedItems: boolean = !isEmpty(linkingItems)

  return (
    <AdditionalInfo
      $isLimitReached={nodeEvents.assignedPersons.length > (isReview ? 2 : 4)}>
      {isNodeHaveLinkedItems && (
        <LinkIconWithCounter $background={background} $isRootObjective={isRoot && isObjective}>
          <div>{linkingItems.length}</div>
          <LinkIcon $background={background} $isLinked $isRootObjective={isRoot && isObjective} />
        </LinkIconWithCounter>
      )}

      {nodeEvents?.period && (
        <Chip
          rounded
          label={nodeEvents.period.chipLabel}
          chipColor={nodeEvents.period.chipColor}
          icon={<WatchLaterIcon style={ICON_STYLE} />}
        />
      )}

      {nodeEvents?.action && (
        <Chip
          rounded
          label={nodeEvents.action.chipLabel}
          chipColor={nodeEvents.action.chipColor}
          icon={<WatchLaterIcon style={ICON_STYLE} />}
        />
      )}

      {nodeEvents?.review && (
        <Chip
          rounded
          icon={<ReviewIcon />}
          label={nodeEvents.review.chipLabel}
          chipColor={nodeEvents.review.chipColor}
        />
      )}

      {nodeEvents.assignedPersons.length >= 1 && (
        <AvatarWrapper>
          <AvatarsList list={nodeEvents.assignedPersons} limit={isReview ? 1 : 3} />
        </AvatarWrapper>
      )}

      <PopoverModal
        disableEnforceFocus
        disableRestoreFocus
        trigger={
          node.plan.locked ? null : <DotsIcon $background={background} $isRootObjective={isObjective && isRoot} />
        }
        PaperProps={PAPER_PROPS}>
        {({ closePopup }) => (
          <PlanItemActionsMenu
            node={node}
            tree={tree}
            isKPI={isKPI}
            isAction={isAction}
            closePopup={closePopup}
            isObjective={isObjective}
            handleKPIAdd={handleKPIAdd}
            handleActionAdd={handleActionAdd}
            handleObjectiveAdd={handleObjectiveAdd}
            nextLevelDescription={nextLevelDescription}
            shouldShowAddKPIButton={shouldShowAddKPIButton}
            shouldShowAddActionButton={shouldShowAddActionButton}
            shouldShowAddObjectiveButton={shouldShowAddObjectiveButton}
          />
        )}
      </PopoverModal>
    </AdditionalInfo>
  )
}

export default NodeInfo
